import React from 'react'
import { Dialog, Divider, Button, Box, Link } from '@material-ui/core'
import { ClassNames } from '@emotion/react'
import i18n from 'common/util/internationalization'
import { AIGenerationIcon } from 'assets/icons/AiGenerationIcon'
import LinearIndeterminate from 'components/shared/LinearProgress'
import { ColorBaseMulberryBlue, ColorBaseWhite } from 'assets/styles/variables'
import {
    HeaderText,
    ProgressMessage,
    aiDialogContainer,
    aiDialogHeader,
    aiGenerateFooter,
    customButton
} from '../PregeneratedMinutesStyles'
import { makeStyles } from '@material-ui/core/styles'
import {
    PREGENERATION_DOCUMENT_STATUS,
    IN_PROGRESS_STATUSES
} from '../../components/InvitationView/types'
import { getLocalStorageItem } from 'businesslayer/minutesLocalStore'
import { getLearnMoreURL } from 'common/util/urlMap'

const useStyles = makeStyles({
    myButton: {
        '&:focus, &:active': {
            boxShadow: `0px 0px 0px 1px ${ColorBaseWhite}, 0px 0px 0px 3px ${ColorBaseMulberryBlue}`,
            borderRadius: '2px'
        }
    },
    inaccuraciesContainer: {
        display: 'flex',
        padding: '4px',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '4px'
    },
    inaccuraciesText: {
        color: '#1E1E1E',
        fontFamily: 'Source Sans Pro',
        fontSize: '12px',
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: '18px',
        letterSpacing: 0
    },
    learnMoreLink: {
        color: '#385F99',
        fontFamily: 'Source Sans Pro',
        fontSize: '12px',
        fontStyle: 'normal',
        fontWeight: 600,
        lineHeight: '18px',
        letterSpacing: 0,
        textDecoration: 'none',
        cursor: 'pointer',
        '&:hover': {
            textDecoration: 'none'
        }
    }
})

interface AiGenerateDialogProps {
    updateGeneration: (...args: any[]) => void
    updateNavigateOut: (...args: any[]) => void
    pregeneratedMinutes: any
}

const AiGenerateDialog: React.FC<AiGenerateDialogProps> = (props) => {
    const classes = useStyles(props)
    const userLanguage = getLocalStorageItem('userLanguage')

    const getProgressMessage = () => {
        const messageMap = {
            [PREGENERATION_DOCUMENT_STATUS.PROGRESS]: 'AI_BOOK_SUMMARY_THINKING',
            [PREGENERATION_DOCUMENT_STATUS.SUMMARY_IN_PROGRESS]: 'AI_BOOK_SUMMARY_IN_PROGRESS',
            [PREGENERATION_DOCUMENT_STATUS.MINUTES_AI_IN_PROGRESS]:
                'AI_MINUTES_GENERATION_IN_PROGRESS'
        }

        const key = messageMap[props.pregeneratedMinutes] ?? 'AI_BOOK_SUMMARY_GENERATE_ALT'
        return i18n.t(key)
    }

    const isDialogOpen = IN_PROGRESS_STATUSES.has(props.pregeneratedMinutes)

    return (
        <ClassNames>
            {({ css }) => (
                <Dialog
                    open={isDialogOpen}
                    className={css`
                        .MuiPaper-root.MuiDialog-paper {
                            max-width: 1000px;
                            width: 1000px;
                            max-height: 100%;
                            border-radius: 8px;
                        }
                    `}>
                    <div className={aiDialogContainer}>
                        <div className={aiDialogHeader}>
                            <AIGenerationIcon />
                            <HeaderText>{i18n.t('AI_BOOK_SUMMARY_GENERATE')}</HeaderText>
                        </div>
                        <Divider />
                        <ProgressMessage>
                            {getProgressMessage()}
                            <br />
                            <br />
                            <LinearIndeterminate loading={true} />
                        </ProgressMessage>
                        <Divider />
                        <Box
                            className={aiGenerateFooter}
                            style={{
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                display: 'flex'
                            }}>
                            <Box className={classes.inaccuraciesContainer}>
                                <span className={classes.inaccuraciesText}>
                                    {i18n.t('AI_INACCURACIES')}
                                </span>

                                <Link
                                    className={classes.learnMoreLink}
                                    style={{ textDecoration: 'none' }}
                                    href={getLearnMoreURL(userLanguage)}
                                    target="blank">
                                    {i18n.t('MINUTES_TEMPLATES_LEARN_MORE')}
                                </Link>
                            </Box>
                            <Box style={{ display: 'flex', gap: '8px' }}>
                                <Button
                                    disableRipple
                                    disableFocusRipple
                                    className={`${classes.myButton} ${customButton}`}
                                    style={{ color: '#455D82', border: '1px solid #455D82' }}
                                    onClick={() => props.updateNavigateOut(true)}
                                    data-analytics="MM-AI-PreGenerated-GoToListPage">
                                    {i18n.t('NOT_FOUND_LINK')}
                                </Button>
                                <Button
                                    disableRipple
                                    disableFocusRipple
                                    className={`${classes.myButton} ${customButton}`}
                                    style={{ backgroundColor: '#455D82', color: '#ffffff' }}
                                    onClick={() => props.updateGeneration('canceled')}
                                    data-analytics="MM-AI-PreGenerated-Cancel">
                                    {i18n.t('AI_BOOK_SUMMARY_GENERATE_CANCEL_BUTTON')}
                                </Button>
                            </Box>
                        </Box>
                    </div>
                </Dialog>
            )}
        </ClassNames>
    )
}

export default AiGenerateDialog
